/**
 * FloorPlanAssembler: the public component for laying a property's rooms
 * out into a whole-property floor plan. The sibling of FloorPlanEditor one
 * abstraction level up: the atoms are whole rooms (moved, rotated, assigned
 * to levels), never their geometry. Editing a room happens in the editor.
 *
 * Owns the assembler state (`useCreateFloorPlanAssemblerState`) and provides
 * it through FloorPlanAssemblerStateContext; every descendant reads it with
 * useFloorPlanAssemblerState(), and the 3D view's camera controls with
 * useView3D(). The assembler ships the editor's default 3D look, with no
 * render-options tray. Self-contained embeddable layout (fills its host):
 * center stage (2D plan / 3D view, level switcher) · right Details panel.
 */

import { css } from '@emotion/react';
import {
  assemblyLevels,
  type AssemblyRoom,
  changedRoomsToSave,
  cssColor,
  type DescribeAssemblyRoom,
  type FloorPlanAssemblerState,
  FloorPlanAssemblerStateContext,
  getFloorPlanPreferences,
  levelRooms,
  type OpeningView,
  PALETTE,
  useCreateFloorPlanAssemblerState,
  useFloorPlanPreferences,
  useSaveHandle,
} from '@kaiizen/kai-floor-plan-editor-core';
import { useImperativeHandle } from 'react';

import { AssemblerSidePanel, AssemblyPlanStage, AssemblyView3DCanvas, AssemblyViewOverlay } from './components';
import { downloadAssemblyModel } from './downloadRoomModel';
// Keeps the preferences in localStorage.
import './preferences';
import { fBody, ink } from './styles';
import { useStageSize } from './useStageSize';
import { useUndoRedoShortcuts } from './useUndoRedoShortcuts';
import { View3DProvider } from './view3d';

const FLOOR_PLAN_ASSEMBLER_ROOT_STYLE = css`
  label: FLOOR_PLAN_ASSEMBLER_ROOT_STYLE;
  display: flex; width: 100%; height: 100%; min-height: 0; min-width: 0;
  font: 14px/1.4 ${fBody};
  color: ${ink};
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  /* A drag that misses a handle must never start selecting text; typing into a field still works. */
  user-select: none; -webkit-user-select: none;
  input, textarea, select { user-select: text; -webkit-user-select: text; }
`;

const ASSEMBLY_STAGE_STYLE = css`
  label: ASSEMBLY_STAGE_STYLE;
  position: relative; flex: 1; min-width: 0; background: ${cssColor(PALETTE.stage)};
`;

/** The assembler's imperative surface, reached through the `ref` prop; `save()` is the Save button's action. */
export interface FloorPlanAssemblerHandle {
  /** Save now, if there is anything to save; false when already clean or without `onSave`. */
  save: () => boolean;
}

export interface FloorPlanAssemblerProps {
  /** The imperative handle (React 19 ref-as-prop). */
  ref?: React.Ref<FloorPlanAssemblerHandle>;
  /**
   * The property's rooms as saved: id (the host's key), display name, and the
   * room's floor plan carrying its placement. Live: a new list is merged in
   * (unsaved placements kept) and becomes the saved baseline.
   */
  rooms: AssemblyRoom[];
  /**
   * Enables the Save button. Receives only the rooms whose placement differs
   * from the saved rooms, each with its optimized floor plan; the host
   * persists them in one save. Omit to hide the button.
   */
  onSave?: (changedRooms: AssemblyRoom[]) => void;
  /**
   * “Edit room” on the selected room: the host opens that room in the editor,
   * passing `view` on as the editor's `defaultView`, so a room opened from 3D
   * opens in 3D, looking at the room from the same side.
   */
  onOpenRoom?: (id: string, view: OpeningView) => void;
  /** Fired whenever the unsaved-changes state flips (hosts wire navigation guards to it). */
  onDirtyChange?: (isDirty: boolean) => void;
  /** Read-only: rooms stay selectable and inspectable, nothing can be moved or saved. */
  readOnly?: boolean;
  /** What the copied JSON says about each room after its id and ahead of its floor plan; its name when omitted. */
  describeRoom?: DescribeAssemblyRoom;
  /** Advanced mode adds the whole-plan JSON (download and copy) to the export menu. */
  advanced?: boolean;
  /** Dev/test seam: receives the assembler state (used by E2E; optional). */
  floorPlanAssemblerStateRef?: React.MutableRefObject<FloorPlanAssemblerState | null>;
}

export function FloorPlanAssembler(
  { ref, rooms, onSave, onOpenRoom, onDirtyChange, readOnly = false, describeRoom, advanced = false, floorPlanAssemblerStateRef }: FloorPlanAssemblerProps,
): React.JSX.Element {
  const { snapping } = useFloorPlanPreferences();
  const floorPlanAssemblerState = useCreateFloorPlanAssemblerState(rooms, readOnly, snapping);
  const { stageRef, stageSize } = useStageSize();
  const { st } = floorPlanAssemblerState;

  if (floorPlanAssemblerStateRef) floorPlanAssemblerStateRef.current = floorPlanAssemblerState;

  useUndoRedoShortcuts(floorPlanAssemblerState, readOnly);

  const save = useSaveHandle(floorPlanAssemblerState, { onSave, onDirtyChange, readOnly }, () => changedRoomsToSave(floorPlanAssemblerState));
  useImperativeHandle(ref, () => ({ save }), [save]);

  return (
    <FloorPlanAssemblerStateContext.Provider value={floorPlanAssemblerState}>
      <View3DProvider>
        <div css={FLOOR_PLAN_ASSEMBLER_ROOT_STYLE} data-testid='floor-plan-assembler'>
          <div css={ASSEMBLY_STAGE_STYLE} ref={stageRef}>
            <AssemblyPlanStage stageW={stageSize.w} stageH={stageSize.h} visible={st.mode === '2d'} />
            <AssemblyView3DCanvas visible={st.mode === '3d'} />
            <AssemblyViewOverlay
              mode={st.mode}
              onMode={floorPlanAssemblerState.setMode}
              level={st.level}
              levels={assemblyLevels(st.rooms)}
              onLevel={floorPlanAssemblerState.setLevel}
              onExport={format => downloadAssemblyModel(levelRooms(st), format, st.level, getFloorPlanPreferences())}
              describeRoom={describeRoom}
              advanced={advanced}
            />
          </div>
          <AssemblerSidePanel
            onSave={onSave && !readOnly ? save : undefined}
            onOpenRoom={onOpenRoom}
          />
        </div>
      </View3DProvider>
    </FloorPlanAssemblerStateContext.Provider>
  );
}
